import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Star, ArrowRight, Quote } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { track } from "@/lib/analytics";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CourseCard } from "@/components/site/CourseCard";
import { CarouselRow } from "@/components/site/CarouselRow";
import { WhyEpicFade, MeetInstructor, WhatYoullLearn, HowItWorks, MotivationalBanner, CommunityPromo, StudentResults } from "@/components/site/EditorialSections";
import { HeroVideo } from "@/components/site/HeroVideo";
import { Button } from "@/components/ui/button";

const HERO_FALLBACK = "https://images.pexels.com/photos/4625626/pexels-photo-4625626.jpeg";
const FALLBACK_ORDER = ["hero", "promo_banners", "featured", "why", "popular", "instructor", "free", "skills", "new", "how", "community", "portfolio", "reviews", "motivation", "cta"];

const HERO_SIZE = { small: "max-w-[240px]", medium: "max-w-[300px]", large: "max-w-[380px]" };
const HERO_HEIGHT = { compact: "py-10 md:py-16", standard: "py-16 md:py-24", tall: "py-24 md:py-36" };
const ASPECT = { "9:16": "aspect-[9/16]", "16:9": "aspect-video", "1:1": "aspect-square", "4:5": "aspect-[4/5]" };
const GRID_D = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" };
const GRID_M = { 1: "grid-cols-1", 2: "grid-cols-2" };
const SPACING = { compact: "py-1", normal: "", spacious: "py-12" };

const CardList = (arr) => arr?.map((c) => (
  <div key={c.id} className="w-[280px] shrink-0 sm:w-[320px]"><CourseCard course={c} /></div>
));

export default function Home() {
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "1";
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onR); return () => window.removeEventListener("resize", onR);
  }, []);
  const { data } = useQuery({ queryKey: ["homepage", isPreview], queryFn: async () => (await api.get("/catalog/homepage", { params: isPreview ? { draft: 1 } : {} })).data });
  useEffect(() => { track("page_view", { page: "home" }); }, []);

  const hero = data?.hero || {};
  const content = data?.content || {};
  const heroImg = hero.hero_image ? resolveImg(hero.hero_image) : HERO_FALLBACK;

  const RENDERERS = {
    courseRow: (key, testid, items) => {
      const s = content[key] || {};
      const max = s.max ? Number(s.max) : (items?.length || 0);
      const list = (items || []).slice(0, max);
      if (!list.length) return null;
      if ((s.layout || "carousel") === "grid") {
        return (
          <section key={key} data-testid={testid} className="mx-auto max-w-7xl px-6 py-8">
            <div className="mb-5"><h2 className="font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight text-white">{s.title}</h2>{s.subtitle && <p className="mt-1 text-sm text-zinc-400">{s.subtitle}</p>}</div>
            <div className={`grid gap-5 ${GRID_M[s.cols_mobile || 2] || "grid-cols-2"} ${GRID_D[s.cols_desktop || 3] || "md:grid-cols-3"}`}>
              {list.map((c) => <CourseCard key={c.id} course={c} />)}
            </div>
          </section>
        );
      }
      return <CarouselRow key={key} testid={testid} title={s.title} subtitle={s.subtitle}>{CardList(list)}</CarouselRow>;
    },
    hero: () => (
      <section key="hero" className="relative grain overflow-hidden border-b border-white/10">
        <div className={`mx-auto grid max-w-7xl items-center gap-10 px-6 md:grid-cols-2 ${HERO_HEIGHT[hero.hero_height] || HERO_HEIGHT.standard}`}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block rounded-full border border-[#DCA846]/30 bg-[#DCA846]/10 px-4 py-1 text-xs uppercase tracking-widest text-[#DCA846]">
              {hero.eyebrow || "Premium Barber Education"}
            </span>
            <h1 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tighter text-white sm:text-6xl md:text-7xl">
              {hero.headline}
            </h1>
            {hero.subheadline ? <p className="mt-4 max-w-lg font-display text-lg uppercase tracking-wide text-[#DCA846]">{hero.subheadline}</p> : null}
            <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-300">{hero.description}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={hero.cta_primary_link || "/store"} onClick={() => track("cta_click", { cta: "primary" })}>
                <Button data-testid="hero-cta-primary" className="bg-[#DCA846] px-7 py-6 text-black hover:bg-[#C5963B] font-display uppercase tracking-wide rounded-sm gold-glow">
                  {hero.cta_primary_text} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to={hero.cta_secondary_link || "/store?free=true"}>
                <Button data-testid="hero-cta-secondary" variant="outline" className="border-white/20 bg-transparent px-7 py-6 text-white hover:bg-white/10 font-display uppercase tracking-wide rounded-sm">
                  <Play className="mr-2 h-4 w-4" /> {hero.cta_secondary_text}
                </Button>
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
            className={`relative mx-auto w-full ${HERO_SIZE[hero.hero_media_size] || HERO_SIZE.medium}`}>
            <div className="relative aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 gold-glow">
              {hero.hero_video ? (
                <HeroVideo src={hero.hero_video} poster={heroImg} />
              ) : (
                <img src={heroImg} alt="Barber craft" className="h-full w-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 text-xs text-white">
                <span className="flex h-2 w-2 rounded-full bg-[#DCA846] animate-pulse" /> Cinematic 9:16 Training
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    ),
    promo_banners: () => data?.promo_banners?.length > 0 && (
      <div key="promo" className="mx-auto max-w-7xl px-6 pt-10">
        {data.promo_banners.map((b, i) => (
          <div key={i} className="mb-4 rounded-lg border border-[#DCA846]/30 bg-gradient-to-r from-[#DCA846]/10 to-transparent p-5">
            <p className="font-display uppercase tracking-wide text-[#DCA846]">{b.title}</p>
            <p className="text-sm text-zinc-300">{b.text}</p>
          </div>
        ))}
      </div>
    ),
    featured: () => RENDERERS.courseRow("featured", "row-featured", data?.featured),
    why: () => <WhyEpicFade key="why" c={content.why} />,
    popular: () => RENDERERS.courseRow("popular", "row-popular", data?.popular),
    instructor: () => <MeetInstructor key="instructor" c={data?.instructor} />,
    free: () => RENDERERS.courseRow("free", "row-free", data?.free),
    skills: () => <WhatYoullLearn key="skills" c={content.skills} />,
    new: () => RENDERERS.courseRow("new", "row-new", data?.new_releases),
    how: () => <HowItWorks key="how" c={content.how} />,
    community: () => <CommunityPromo key="community" c={content.community} />,
    portfolio: () => data?.portfolio?.length > 0 && (
      <section key="portfolio" className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-display text-3xl font-semibold uppercase tracking-tight">{content.portfolio?.title || "My Work"}</h2>
          <Link to="/portfolio" className="text-sm uppercase tracking-wider text-[#DCA846]">View all →</Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {data.portfolio.slice(0, 6).map((p) => (
            <div key={p.id} className="aspect-square overflow-hidden rounded-lg border border-white/10">
              <img src={resolveImg(p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover hover:scale-110 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </section>
    ),
    reviews: () => data?.reviews?.length > 0 && (
      <CarouselRow key="reviews" testid="row-reviews" title={content.reviews?.title} subtitle={content.reviews?.subtitle}>
        {data.reviews.map((r) => (
          <div key={r.id} className="w-[300px] shrink-0 rounded-lg border border-white/10 bg-zinc-900/50 p-6 sm:w-[340px]">
            <Quote className="h-8 w-8 text-[#DCA846]/40" />
            <p className="mt-3 text-sm leading-relaxed text-zinc-300">{r.text}</p>
            <div className="mt-4 flex items-center gap-3">
              {r.avatar ? <img src={resolveImg(r.avatar)} alt="" className="h-9 w-9 rounded-full object-cover" /> :
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#DCA846]/20 text-[#DCA846] font-display">{r.name?.[0]}</div>}
              <div>
                <p className="text-sm font-semibold text-white">{r.name}</p>
                <div className="flex text-[#DCA846]">{Array.from({ length: Math.round(r.rating || 5) }).map((_, i) => <Star key={i} className="h-3 w-3 fill-[#DCA846]" />)}</div>
              </div>
            </div>
          </div>
        ))}
      </CarouselRow>
    ),
    motivation: () => <MotivationalBanner key="motivation" c={content.motivation} />,
    results: () => <StudentResults key="results" items={data?.student_results || []} c={content.results} />,
    cta: () => (
      <section key="cta" className="mx-auto my-16 max-w-7xl px-6">
        <div className="grain relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/50 p-10 text-center md:p-16">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight md:text-4xl">{content.cta?.title || "Ready to master the fade?"}</h2>
          <p className="mx-auto mt-3 max-w-xl text-zinc-400">{content.cta?.body || "Join Epic Fade Academy and get access to premium, cinematic barber training."}</p>
          <Link to={content.cta?.button_link || "/store"}><Button className="mt-6 bg-[#DCA846] px-8 py-6 text-black hover:bg-[#C5963B] font-display uppercase tracking-wide rounded-sm">{content.cta?.button_text || "Explore Courses"}</Button></Link>
        </div>
      </section>
    ),
  };

  const order = (data?.sections && data.sections.length ? data.sections : FALLBACK_ORDER.map((k) => ({ key: k, visible: true })))
    .filter((s) => s.visible !== false && (isMobile ? s.mobile !== false : s.desktop !== false));

  return (
    <SiteLayout>
      {isPreview && (
        <div data-testid="preview-banner" className="sticky top-0 z-50 bg-[#DCA846] px-4 py-2 text-center text-xs font-display uppercase tracking-wider text-black">
          Preview Mode — showing unpublished draft
        </div>
      )}
      {order.map((s) => {
        const node = RENDERERS[s.key] ? RENDERERS[s.key]() : null;
        if (!node) return null;
        const d = s.design || {};
        const cls = [SPACING[d.spacing] || "", d.background === "panel" ? "bg-zinc-900/30" : "", d.align === "center" ? "text-center" : ""].join(" ").trim();
        return cls ? <div key={s.key} data-testid={`sec-wrap-${s.key}`} className={cls}>{node}</div> : node;
      })}
    </SiteLayout>
  );
}
